import { useMemo, useEffect } from 'react'
import { useAppSelector, useAppDispatch } from 'hooks/reduxHooks'
import { buildSearchParamsKey } from 'lib/searchUtils'
import { getBundlePackagesFilteredByRate } from 'lib/bundleAndSave/getBundlePackagesFilteredByRate'
import { min } from 'lib/arrayUtils'
import { fetchBestPriceForOffer } from 'actions/OfferActions'

function useBestPriceBundleAndSavePackage(
  offer: App.OfferBundleAndSave,
  bundledOfferId: string,
  useBestPrice: boolean,
  filters?: App.OfferListFilters,
): {
  bestPricePackage: App.BundlePackageWithPrice,
  bestPriceError: any,
  fetching: boolean,
  soldOut: boolean,
  available: boolean,
} {
  const dispatch = useAppDispatch()
  const bestPrices = useAppSelector(state => state.offer.offerBestPrices[offer?.id]) ?? {}
  const pricesErrors = useAppSelector(state => state.offer.offerPricesErrors[offer?.id]) ?? {}

  const searchKey = useMemo(() => buildSearchParamsKey(filters.checkIn, filters.checkOut, filters.rooms, bundledOfferId), [filters, bundledOfferId])

  const bestPrice = bestPrices[searchKey]
  const bestPriceError = pricesErrors[searchKey]
  const available = bestPrice?.available
  const rate = available ? bestPrice.rate : undefined
  const fetching = useBestPrice && !bestPrice && !bestPriceError
  const soldOut = useBestPrice && bestPrice && !available

  const bestPricePackage = useMemo(() => {
    if (!useBestPrice || bestPriceError) {
      return offer?.lowestPriceBundlePackage
    }
    if (available && 'packages' in offer) {
      const bundlePackagesWithPrice = getBundlePackagesFilteredByRate(offer, rate)

      return min(bundlePackagesWithPrice, pkg => pkg.price || Number.MAX_SAFE_INTEGER)
    }
  }, [offer, available, rate, useBestPrice, bestPriceError])

  useEffect(() => {
    if (useBestPrice && bundledOfferId && offer) {
      dispatch(fetchBestPriceForOffer(offer, {
        checkIn: filters.checkIn,
        checkOut: filters.checkOut,
        occupants: filters.rooms,
        bundledOfferId,
      }))
    }
  }, [dispatch, filters, offer, useBestPrice, bundledOfferId])

  return {
    bestPricePackage,
    bestPriceError,
    fetching,
    soldOut,
    available,
  }
}

export default useBestPriceBundleAndSavePackage
